import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '../../../basic/Button';
import Typography from '../../../basic/Typography';
import { notifyApiKeyCreated } from '../services/hostBridge';
import { approveDeviceCode, getTopUpSession } from '../services/authApi';
import { trackAuthEvent } from '../services/authTelemetry';
import { useAuth } from '../context/AuthContext';
import { formatCreditsAsUsd } from '../utils/credits';
import { AuthLayout } from './AuthLayout';
import formStyles from './AuthForm.module.css';
import styles from './DevicePage.module.css';

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function useQueryParam(name: string): string | null {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get(name), [name, search]);
}

type CopyState = 'idle' | 'copied';

function CopyButton({ text, label }: { text: string; label: string }) {
  const [state, setState] = useState<CopyState>('idle');

  const onClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      // no-op
    }
  }, [text]);

  return (
    <button
      type="button"
      className={styles.copyBtn}
      onClick={() => void onClick()}
      title={label}
      data-copied={state === 'copied' ? 'true' : undefined}
    >
      {state === 'copied' ? <CheckIcon /> : <CopyIcon />}
      <span>{state === 'copied' ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

export function DevicePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const userCode = useQueryParam('user_code') ?? '';

  const [apiKey, setApiKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onApprove = useCallback(async () => {
    if (!userCode) {
      setError('missing_user_code');
      trackAuthEvent({ name: 'device_code_failed', error: 'missing_user_code' });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await approveDeviceCode(userCode);
      setApiKey(res.api_key);
      notifyApiKeyCreated(res.api_key);
      trackAuthEvent({ name: 'device_code_approved' });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'approve_failed';
      setError(errMsg);
      trackAuthEvent({ name: 'device_code_failed', error: errMsg });
    } finally {
      setIsLoading(false);
    }
  }, [userCode]);

  const location = useLocation();
  if (auth.status === 'anonymous') {
    const returnUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return (
    <AuthLayout title="Pair your IDE">
      <div className={styles.page}>
        <Typography variant="body1" color="secondary" align="center">
          Confirm the code matches your IDE, then approve to issue an API key.
        </Typography>

        {/* Verification Code Card */}
        <div className={styles.codeCard}>
          <div className={styles.cardHeader}>
            <Typography variant="caption" color="secondary">
              Verification code
            </Typography>
          </div>
          <div className={styles.codeRow}>
            <code className={styles.codeDisplay}>{userCode || '--------'}</code>
            {userCode && <CopyButton text={userCode} label="Copy verification code" />}
          </div>
          {!userCode && (
            <Typography variant="caption" color="secondary">
              Code missing? Re-open the link from your IDE.
            </Typography>
          )}
        </div>

        {error && (
          <div className={formStyles.error} role="alert">
            <Typography variant="body2">{error}</Typography>
          </div>
        )}

        {apiKey ? (
          <>
            {/* Success Card */}
            <div className={styles.successCard}>
              <div className={styles.successHeader}>
                <Typography variant="h4">Paired successfully</Typography>
                <Typography variant="body2" color="secondary">
                  Your IDE now has an API key. You can close this tab.
                </Typography>
              </div>

              <div className={styles.cardHeader}>
                <Typography variant="caption" color="secondary">
                  API key
                </Typography>
              </div>
              <div className={styles.codeRow}>
                <code className={styles.apiKeyDisplay}>{apiKey}</code>
                <CopyButton text={apiKey} label="Copy API key" />
              </div>
            </div>

            {/* Account Info Card */}
            {auth.me && (
              <div className={styles.infoCard}>
                <div className={styles.infoRow}>
                  <Typography variant="caption" color="secondary">
                    Account
                  </Typography>
                  <Typography variant="body2">{auth.me.email || auth.me.name}</Typography>
                </div>
                {auth.me.balance !== null && (
                  <div className={styles.infoRow}>
                    <Typography variant="caption" color="secondary">
                      Balance
                    </Typography>
                    <Typography variant="body2">{formatCreditsAsUsd(auth.me.balance)}</Typography>
                  </div>
                )}
              </div>
            )}

            {/* Low Balance Warning */}
            {auth.me?.low_balance && (
              <div className={styles.warningBanner}>
                <Typography variant="body2">Low credits! Top up to continue using the API.</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    void getTopUpSession(window.location.href, 10000).then((res) => {
                      window.location.href = res.url;
                    });
                  }}
                >
                  Top up
                </Button>
              </div>
            )}

            <div className={styles.actionsRow}>
              <Button variant="contained" color="primary" onClick={() => navigate('/auth/account')}>
                Go to account
              </Button>
            </div>
          </>
        ) : (
          <div className={styles.actionsRow}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={isLoading || !userCode}
              onClick={() => void onApprove()}
            >
              {isLoading ? 'Approving...' : 'Approve device'}
            </Button>
            <Link to="/auth/account" className={styles.cancelLink}>
              Cancel
            </Link>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
